import { useState } from 'react';
import { ExpandDown } from '../../../../assets/CustomIcons/ExpandDown';
import BaseDropdown from '../../../../components/UI/Dropdown/BaseDropdown';
import StatusLabel, { StatusComponentType } from '../../../../components/UI/StatusLabel';
import { TaskStatus, TaskStatusGroup, TaskStatusGroupUtils } from '../../../../types/TaskStatus';
import { GithubStatusRule, GithubStatusRuleEvent, GithubStatusRuleInput } from '../../../../types/Github';
import { groupBy } from '../../../../utils/arrayUtils';

/**
 * The picker is two-valued: "no change", or a specific status.
 *
 * The WIRE format still carries three states, and that asymmetry is deliberate. The
 * server distinguishes `enabled: false` from `enabled: true` with no `statusId`, but
 * `resolveTargetStatus` resolves both to "move nothing" now that there are no built-in
 * group defaults, so offering them as two rows would be two rows that do the same thing.
 * `enabled` still matters to the server - it gates the assignee - so the flag is written,
 * not dropped. "No change" with an assignee is written as enabled, because an owner
 * applies on its own; without one it stays disabled, as before.
 *
 *   RULE_VALUE_NONE -> { enabled: assigneeId !== null, statusId: null }
 *   "<statusId>"    -> { enabled: true,  statusId: N }
 *
 * Values are strings because the special shares the space with numeric status ids.
 */
export const RULE_VALUE_NONE = 'none';

/** Both "off" and "on with no status" read back as no change - see above. */
export const ruleToValue = (rule: GithubStatusRule): string => {
  if (!rule.enabled || rule.statusId === null) return RULE_VALUE_NONE;
  return String(rule.statusId);
};

/**
 * `assigneeId` travels alongside the status because the endpoint is a batch replace of
 * whole rules: omitting it from an otherwise unchanged row would clear it.
 */
export const valueToRuleInput = (
  event: GithubStatusRuleEvent,
  value: string,
  assigneeId: number | null
): GithubStatusRuleInput => {
  if (value === RULE_VALUE_NONE) return { event, statusId: null, assigneeId, enabled: assigneeId !== null };
  return { event, statusId: Number(value), assigneeId, enabled: true };
};

export type GithubStatusRuleSelectProps = {
  rule: GithubStatusRule;
  value: string;
  statuses: TaskStatus[];
  disabled?: boolean;
  onChange: (value: string) => void;
};

/** Grey text for the two choices that are not a status. Matches the empty-state copy
 *  the property dropdowns use ("No epic", "Assign Epic"). */
const PlainOption = ({ children }: { children: React.ReactNode }) => (
  <span className="truncate text-sm font-normal leading-tight text-neutral-900">{children}</span>
);

/**
 * The status picker for one pull request event.
 *
 * Built on `BaseDropdown` with a label-plus-chevron trigger, which is how every other
 * status picker in the product works - `TaskStatusDropdown` on a task, and
 * `FrameworkStatusLabelDropdown` inside this very modal. It deliberately does NOT use an
 * antd `Select`: that renders a form-field box with its own chevron and hover treatment,
 * which is not what a status looks like anywhere else in the app.
 */
export const GithubStatusRuleSelect = ({ rule, value, statuses, disabled, onChange }: GithubStatusRuleSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const grouped = groupBy(statuses, 'group');

  const selectedStatus = statuses.find((status) => String(status.id) === value) ?? null;
  // A status can be deleted while a rule still names it. The server resolves that to "no
  // transition", but the trigger would otherwise render the bare id where a name belongs.
  const selectedStatusMissing = value !== RULE_VALUE_NONE && !selectedStatus;

  const select = (next: string) => {
    setIsOpen(false);
    if (next !== value) onChange(next);
  };

  const option = (optionValue: string, label: string, content: React.ReactNode) => (
    <div
      key={optionValue}
      role="option"
      title={label}
      aria-selected={optionValue === value}
      className="flex w-full items-center rounded px-1 py-0.5 cursor-pointer hover:bg-neutral-100"
      onClick={() => select(optionValue)}
    >
      {content}
    </div>
  );

  const trigger = selectedStatus ? (
    <StatusLabel
      color={selectedStatus.color}
      title={selectedStatus.title}
      group={selectedStatus.group}
      component={StatusComponentType.Dropdown}
    />
  ) : (
    <PlainOption>{selectedStatusMissing ? 'Deleted status' : 'No change'}</PlainOption>
  );

  return (
    <BaseDropdown
      disableTransition
      trigger={['click']}
      open={isOpen}
      disabled={disabled}
      placement="bottomLeft"
      onOpenChange={setIsOpen}
      dropdownRender={() => (
        <div className="box-border flex w-[240px] flex-col items-start justify-start rounded-lg bg-brand-white p-2 shadow-md">
          <div className="flex w-full max-h-[300px] flex-col items-start justify-start gap-2 overflow-y-auto">
            <div className="flex w-full flex-col gap-1">
              {option(RULE_VALUE_NONE, 'No change', <PlainOption>No change</PlainOption>)}
            </div>

            <div className="h-px w-full shrink-0 bg-neutral-300" />

            {Object.values(TaskStatusGroup).map((group) =>
              (grouped[group] ?? []).length === 0 ? null : (
                <div
                  key={group}
                  className="w-full text-xs font-semibold font-['Figtree'] text-neutral-900 flex flex-col gap-1"
                >
                  {TaskStatusGroupUtils.getTitle(group)}
                  {grouped[group].map((status) =>
                    option(
                      String(status.id),
                      status.title,
                      <StatusLabel
                        color={status.color}
                        title={status.title}
                        group={status.group}
                        component={StatusComponentType.Dropdown}
                      />
                    )
                  )}
                </div>
              )
            )}
          </div>
        </div>
      )}
    >
      <div
        role="combobox"
        aria-label={`Status for ${rule.event}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={!!disabled}
        tabIndex={disabled ? -1 : 0}
        // The antd Select this replaced was keyboard-operable; a plain div trigger is not
        // unless it opens on Enter/Space, so the swap does not cost keyboard access.
        onKeyDown={(event) => {
          if (disabled || (event.key !== 'Enter' && event.key !== ' ')) return;
          event.preventDefault();
          setIsOpen(true);
        }}
        className={`flex w-fit max-w-full items-center gap-1 rounded py-0.5 pl-1 pr-0.5 ${
          disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-neutral-200'
        }`}
      >
        {trigger}
        <ExpandDown className="shrink-0 text-neutral-900" />
      </div>
    </BaseDropdown>
  );
};
